import React, { useEffect, useMemo, useState } from "react";
import { Dialog, Button, Checkbox, Flex } from "@radix-ui/themes";
import { AlertTriangle, Sparkles } from "lucide-react";
import RadixModalHeader from "./RadixModalHeader";
import { apiFetch } from "./api.js";
import { useToast } from "./ToastContext";
import fields from "./fields";
import { CancelIcon, ConfirmIcon, ICON_SIZE } from "./actionIcons";

/**
 * Fill specs — review what the vehicle lookup can fill in, then apply it.
 *
 * VEHICLE_DATA_QUALITY_PLAN.md Part 1. The add-vehicle wizard fills Drive,
 * Seats, Doors, body type and the rest from the carmakes lookup; vehicles added
 * any other way never got them, and those are the exact fields the dealership's
 * public website builds its buyer filters from. On production 2026-08-11 that
 * was 94 of 112 vehicles.
 *
 * NOTHING IS APPLIED WITHOUT A HUMAN SAYING SO. The server proposes, this
 * screen shows current-vs-proposed side by side, and only the checked rows are
 * sent back. That is the entire safety argument for a feature that infers:
 * the inference is always visible before it is stored.
 *
 * Two things are deliberately NOT offered:
 *   - Any cost or price field. The lookup knows what a Corolla is; it has no
 *     idea what this dealer paid for one, and a proposal there would be a
 *     fabrication wearing the same UI as a fact.
 *   - Bulk-accept for a vehicle whose chassis number contradicts its Make or
 *     Model. Filling HiAce specs into a row labelled ISUZU CANTER makes the
 *     listing more complete AND more wrong; four production rows share one
 *     HiAce chassis under three different model names. Those must be opened
 *     and decided one at a time.
 */

const TIER_LABEL = {
  1: "Matched by chassis number",
  2: "Matched by make and model",
  3: "No match",
};

const TIER_HINT = {
  1: "The chassis number identifies the model directly. This is the strongest match.",
  2: "Matched on the make and model as typed, because the chassis number didn't resolve. Weaker — it trusts the field most likely to be wrong.",
  3: "Neither the chassis number nor the make/model matched anything in the lookup.",
};

const label = (f) => fields[f]?.displayName || f;

//apiFetch resolves with the raw Response, not the parsed body, and does NOT
//reject on a 4xx/5xx — so an unchecked `.then(res => res.vehicles)` reads
//undefined off a Response object and renders an empty screen where the real
//answer was "permission denied". Same shape as aiApi.js's aiPost.
async function call(path, options) {
  const res = await apiFetch(path, options);
  if (res.ok) return res.json();
  let message = "";
  try { message = (await res.json()).message || ""; } catch { /* not JSON */ }
  throw new Error(message || `Request failed (${res.status})`);
}

export default function EnrichVehiclesModal({ open, onOpenChange, onApplied }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  //vehicleId -> Set of field names the user wants applied.
  const [selection, setSelection] = useState({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    call("/enrichment/suggestions", { method: "GET" })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        //Pre-check everything the lookup is confident about, EXCEPT on vehicles
        //whose identity is in doubt — those start unchecked so accepting them
        //is a deliberate act rather than the default.
        const initial = {};
        for (const v of res.vehicles || []) {
          if (v.identity.makeMismatch || v.identity.modelMismatch) continue;
          const picked = [
            ...Object.keys(v.proposals),
            ...Object.keys(v.assumptions),
          ];
          if (picked.length) initial[v.VehicleID] = new Set(picked);
        }
        setSelection(initial);
      })
      .catch((err) => !cancelled && showToast("error", err.message || "Couldn't load suggestions."))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, showToast]);

  const vehicles = data?.vehicles || [];

  const { vehicleCount, fieldCount } = useMemo(() => {
    let v = 0, f = 0;
    for (const set of Object.values(selection)) {
      if (set.size) { v++; f += set.size; }
    }
    return { vehicleCount: v, fieldCount: f };
  }, [selection]);

  const toggle = (vehicleId, field) => {
    setSelection((prev) => {
      const next = { ...prev };
      const set = new Set(next[vehicleId] || []);
      if (set.has(field)) set.delete(field); else set.add(field);
      if (set.size) next[vehicleId] = set; else delete next[vehicleId];
      return next;
    });
  };

  const apply = async () => {
    const updates = vehicles
      .filter((v) => selection[v.VehicleID]?.size)
      .map((v) => ({
        VehicleID: v.VehicleID,
        fields: Object.fromEntries(
          [...selection[v.VehicleID]].map((f) => [
            f,
            (v.proposals[f] || v.assumptions[f]).value,
          ])
        ),
      }));
    if (!updates.length) return;

    setSaving(true);
    try {
      const res = await call("/enrichment/apply", { body: { updates } });
      showToast("success", `Filled ${res.fields} field${res.fields === 1 ? "" : "s"} across ${res.vehicles} vehicle${res.vehicles === 1 ? "" : "s"}.`);
      onApplied?.();
      onOpenChange(false);
    } catch (err) {
      showToast("error", err.message || "Couldn't apply the changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ padding: 0, overflow: "hidden", maxWidth: 940 }}>
        <RadixModalHeader icon={Sparkles} title="Fill specs from the vehicle lookup" disabled={saving} />

        {/* padding 0: the summary and each vehicle row carry their own, and
            the row separators need to reach the full width. */}
        <div className="modal-shell-body" style={{ maxHeight: "62vh", padding: 0 }}>
          {loading && <p className="hint-t" style={{ padding: 16 }}>Looking up your vehicles…</p>}

          {!loading && data && <Summary summary={data.summary} />}

          {!loading && data && vehicles.length === 0 && (
            <p style={{ padding: 16 }}>
              Every vehicle already has all the details your website filters on. Nothing to fill.
            </p>
          )}

          {!loading && vehicles.map((v) => (
            <VehicleRow
              key={v.VehicleID}
              vehicle={v}
              picked={selection[v.VehicleID] || new Set()}
              onToggle={(f) => toggle(v.VehicleID, f)}
            />
          ))}
        </div>

        <div className="modal-shell-footer">
          <Flex gap="3" justify="between" align="center" style={{ width: "100%" }}>
            <span className="hint-t" style={{ fontVariantNumeric: "tabular-nums" }}>
              {fieldCount
                ? `${fieldCount} field${fieldCount === 1 ? "" : "s"} on ${vehicleCount} vehicle${vehicleCount === 1 ? "" : "s"} selected`
                : "Nothing selected"}
            </span>
            <Flex gap="3">
              <Button variant="soft" color="gray" onClick={() => onOpenChange(false)} disabled={saving}><CancelIcon size={ICON_SIZE} />
                Cancel
              </Button>
              <Button onClick={apply} disabled={saving || !fieldCount}>
                <ConfirmIcon size={ICON_SIZE} />
                {saving ? "Applying…" : "Apply selected"}
              </Button>
            </Flex>
          </Flex>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * The counts that decide whether this screen was worth opening — and, for the
 * residual, WHY each vehicle is in it. "14 unmatched" would be a misleading
 * number: a row whose chassis reads 1Z1234567890 doesn't need a better lookup,
 * it needs its chassis number typed in, and no amount of cleverness elsewhere
 * substitutes for that.
 */
function Summary({ summary }) {
  if (!summary) return null;
  const r = summary.residual;
  return (
    <div style={{
      padding: "12px 16px",
      borderBottom: "1px solid var(--theme-line)",
      background: "var(--theme-surface-alt)",
      fontSize: 13,
    }}>
      <p style={{ margin: 0 }}>
        <strong>{summary.fieldsProposable}</strong> field
        {summary.fieldsProposable === 1 ? "" : "s"} can be filled in across{" "}
        <strong>{summary.incomplete}</strong> incomplete vehicle
        {summary.incomplete === 1 ? "" : "s"}.
      </p>
      {r.total > 0 && (
        <p style={{ margin: "4px 0 0", color: "var(--theme-muted)" }}>
          {r.total} couldn&apos;t be matched
          {r.unusableChassis > 0 && ` — ${r.unusableChassis} because the chassis number is missing or a placeholder`}
          {r.unusableChassis > 0 && r.noLookupMatch > 0 && ", and"}
          {r.noLookupMatch > 0 && ` ${r.noLookupMatch} the lookup simply doesn't cover`}.
        </p>
      )}
      {summary.identityWarnings > 0 && (
        <p style={{ margin: "4px 0 0", color: "var(--theme-danger)" }}>
          <AlertTriangle size={13} style={{ display: "inline", verticalAlign: -2 }} />{" "}
          {summary.identityWarnings} vehicle{summary.identityWarnings === 1 ? " has" : "s have"} a
          chassis number that disagrees with the make or model recorded. Those are left unchecked.
        </p>
      )}
    </div>
  );
}

function VehicleRow({ vehicle: v, picked, onToggle }) {
  const proposable = [
    ...Object.entries(v.proposals).map(([f, p]) => ({ field: f, ...p })),
    ...Object.entries(v.assumptions).map(([f, p]) => ({ field: f, ...p })),
  ];
  const ambiguous = Object.entries(v.ambiguous || {});
  const flagged = v.identity.makeMismatch || v.identity.modelMismatch
    || v.identity.duplicateChassis || v.identity.placeholderChassis;

  //A vehicle with nothing to offer and nothing to warn about is noise on a
  //review screen — it is still counted in the summary above.
  if (!proposable.length && !flagged) return null;

  return (
    <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--theme-line)" }}>
      <Flex justify="between" align="baseline" gap="3" wrap="wrap">
        <div>
          <strong>{[v.ModelYear, v.Make, v.Model].filter(Boolean).join(" ") || "Vehicle"}</strong>
          <span className="hint-t" style={{ marginLeft: 8 }}>{v.ChassisNo || "no chassis number"}</span>
        </div>
        <Flex gap="2" align="center">
          <span className="pill-t pill-t-muted" style={{ fontVariantNumeric: "tabular-nums" }}>
            {v.completeness}% complete
          </span>
          <span className="pill-t pill-t-accent" title={TIER_HINT[v.tier]}>
            {TIER_LABEL[v.tier]}
            {v.tier === 1 && v.match?.code ? ` (${v.match.code})` : ""}
          </span>
        </Flex>
      </Flex>

      <IdentityWarnings vehicle={v} />

      {proposable.length > 0 && (
        <div style={{ marginTop: 8, display: "grid", gap: 4 }}>
          {proposable.map(({ field, value, from }) => (
            <label key={field} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <Checkbox checked={picked.has(field)} onCheckedChange={() => onToggle(field)} />
              <span style={{ minWidth: 130, color: "var(--theme-muted)" }}>{label(field)}</span>
              <span style={{ fontWeight: 500 }}>{String(value)}</span>
              {from === "assumed" && (
                <span className="hint-t">
                  — assumed, not from the lookup (Japanese imports are right-hand drive)
                </span>
              )}
            </label>
          ))}
        </div>
      )}

      {ambiguous.length > 0 && (
        <p className="hint-t" style={{ marginTop: 6 }}>
          <Sparkles size={12} style={{ display: "inline", verticalAlign: -1 }} />{" "}
          Nothing proposed for {ambiguous.map(([f]) => label(f)).join(", ")} — this chassis code
          covers several versions that disagree, so it&apos;s left for you rather than guessed.
        </p>
      )}
    </div>
  );
}

function IdentityWarnings({ vehicle: v }) {
  const notes = [];
  const candidates = (v.match?.candidates || []).slice(0, 3)
    .map((c) => `${c.make} ${c.model}`).join(", ");

  if (v.identity.makeMismatch) {
    notes.push(`The chassis number belongs to a different make${candidates ? ` — the lookup says ${candidates}` : ""}.`);
  } else if (v.identity.modelMismatch) {
    notes.push(`The chassis number belongs to a different model${candidates ? ` — the lookup says ${candidates}` : ""}.`);
  }
  if (v.identity.duplicateChassis) {
    notes.push("This chassis number is on more than one of your vehicles. Only one of them can be right.");
  }
  if (v.identity.placeholderChassis) {
    notes.push("This chassis number looks like a placeholder rather than a real one.");
  }
  if (!notes.length) return null;

  return (
    <div style={{
      marginTop: 8, padding: "6px 10px", fontSize: 13,
      borderLeft: "3px solid var(--theme-danger)",
      background: "var(--theme-surface-alt)",
    }}>
      {notes.map((n, i) => <p key={i} style={{ margin: i ? "4px 0 0" : 0 }}>{n}</p>)}
      <p className="hint-t" style={{ margin: "4px 0 0" }}>
        Fix the vehicle&apos;s details first — filling specs in from the wrong car would make this
        listing look complete while being wrong.
      </p>
    </div>
  );
}
